import { Dayjs } from 'dayjs'
import { NorwegianId } from 'norwegian-national-id-validator'

import { day, diff, format, getToday } from '~/astra'
import { Surgery } from '~/store/selectors'
import { useStore } from '~/store/store'
import { isNotNullish, isNullish } from '~/utils/guards'

type Patient = Surgery['patient']

/** Mask used for personal data hidden by privacy mode. */
export const PRIVACY_MASK = '****'

export function maskIfPrivate(value: string | null | undefined): string | null | undefined {
    const isPrivacyModeEnabled = useStore.getState().app.isPrivacyMode
    return isPrivacyModeEnabled && value ? PRIVACY_MASK : value
}

export function getBirthDate(patient: Patient | undefined): Dayjs | undefined {
    if (isNullish(patient)) {
        return undefined
    }
    if (isNotNullish(patient.birthDate)) {
        return day(patient.birthDate)
    }
    if (isNotNullish(patient.nationalId)) {
        const fromNorwegianId = NorwegianId(patient.nationalId).birthDate()
        return isNotNullish(fromNorwegianId) ? day(fromNorwegianId) : undefined
    }
    return undefined
}

export function getAge(patient: Patient | undefined): number | undefined {
    const birthDate = getBirthDate(patient)
    if (isNotNullish(birthDate)) {
        return diff(getToday(), birthDate, 'year')
    }
    return undefined
}

export function formatAge(patient: Patient | undefined): string {
    const age = getAge(patient)
    return age ? `${age} år` : ''
}

export function getBirthYear(patient: Patient | undefined): string {
    const birthDate = getBirthDate(patient)
    return birthDate ? format(birthDate, 'YYYY') : ''
}

export function formatNationalId(nationalId: string | null | undefined): string {
    if (isNullish(nationalId)) return ''
    return maskIfPrivate(`${nationalId.slice(0, 6)} ${nationalId.slice(6)}`) ?? ''
}

export function formatName(patient: Patient | undefined) {
    const firstName = maskIfPrivate(patient?.firstName) ?? ''
    const lastName = maskIfPrivate(patient?.lastName) ?? ''
    const fullName = [firstName, lastName].filter(Boolean).join(' ')
    const lastNameFirst = firstName && lastName ? `${lastName}, ${firstName}` : ''
    return { firstName, lastName, fullName, lastNameFirst }
}

export function formatLastNameAndFirstName(patient: Patient | undefined) {
    const { firstName, lastName } = formatName(patient)

    return firstName && lastName ? `${lastName}, ${firstName.charAt(0)}` : ''
}

export function formatFullNameAndMunicipality(patient: Patient | undefined) {
    const { fullName } = formatName(patient)
    const municipality = maskIfPrivate(patient?.municipality) ?? ''

    return {
        fullName,
        municipality,
    }
}

export function formatAgeAndNationalIdAndCopyText(patient: Patient | undefined) {
    const age = formatAge(patient)
    const nationalId = formatNationalId(patient?.nationalId)
    const hasNationalId = nationalId !== ''
    const nationalIdCopyText = nationalId.replace(/\s+/g, '')

    return {
        age,
        nationalId,
        hasNationalId,
        nationalIdCopyText,
    }
}

type ArenaPhoneNumber = NonNullable<NonNullable<Patient>['arenaPhoneNumbers']>[number]

type FormatPhoneNumberArgs = {
    arenaPhoneNumber: ArenaPhoneNumber | undefined
    withPrefix?: boolean
}

export const formatPhoneNumber = ({ arenaPhoneNumber, withPrefix = false }: FormatPhoneNumberArgs) => {
    if (!arenaPhoneNumber) return ''

    const prefix = withPrefix ? 'Tlf. ' : ''
    const countryCode = arenaPhoneNumber.countryCode ? `${arenaPhoneNumber.countryCode} ` : ''

    return `${prefix}${maskIfPrivate(`${countryCode}${arenaPhoneNumber.phoneNumber}`)}`
}

type FormatPhoneNumbersArgs = {
    patient: Patient
    withPrefix?: boolean
}

export const formatPhoneNumbers = ({ patient, withPrefix = false }: FormatPhoneNumbersArgs) =>
    (patient?.arenaPhoneNumbers ?? []).flatMap(phone => {
        if (!phone.phoneNumber) return []

        return [{ phoneNumber: formatPhoneNumber({ arenaPhoneNumber: phone, withPrefix }), type: phone.type }]
    })

export const formatCancerPathway = (patient?: Patient) => {
    const cancerPathway = patient?.cancer_patient_pathway

    return {
        isCancerPathway: Boolean(cancerPathway),
        cancerPathway: cancerPathway ?? '',
    }
}

export const formatLanguageAndInterpreter = (patient: Patient | undefined) => {
    const preferredLanguage = maskIfPrivate(patient?.preferredLanguage)
    const interpreterTypeNeeded = maskIfPrivate(patient?.interpreterTypeNeeded)
    const interpreterLanguage = maskIfPrivate(patient?.interpreterLanguage)

    return {
        preferredLanguage,
        interpreterTypeNeeded,
        interpreterLanguage,
    }
}
